import React, {
  useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Link, useNavigate, useParams, useSearchParams,
} from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FaArrowLeft,
  FaBan,
  FaCheckCircle,
  FaClock,
  FaCrown,
  FaFlask,
  FaPlay,
  FaShieldAlt,
  FaStop,
} from 'react-icons/fa';
import { AuthContext } from './AuthContext';
import NavigationBar from './components/NavigationBar';
import LoadingSpinner from './components/LoadingSpinner';
import useDebouncedValue from './hooks/useDebouncedValue';
import usePremiumEntitlement from './hooks/usePremiumEntitlement';
import {
  TERMINAL_BACKTEST_STATES,
  backtestErrorMessage,
  backtestSnapshotKey,
  buildBacktestRequest,
  chartPoints,
  dateOnly,
  eligibleBacktestRange,
  isBacktestRangeOutsideEligible,
  isPremiumPlan,
  presentBacktestResult,
  selectBacktestSnapshot,
  snapshotUnavailabilityMessage,
  summarizeDecisions,
  validateBacktestDraft,
} from './backtests';
import { ownedPreselectedBacktestBotId } from './backtestHandoff';
import { strategyDisplayName } from './beginnerEducation';
import StrategyResearchBeta from './StrategyResearchBeta';

const card = 'rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900';
const input = 'mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-slate-950 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:focus:ring-blue-950';

const money = (value) => new Intl.NumberFormat(undefined, {
  style: 'currency', currency: 'USD', maximumFractionDigits: 2,
}).format(Number(value || 0));
const tradePrice = (value) => (
  value === null || value === undefined || !Number.isFinite(Number(value))
    ? '—'
    : money(value)
);
const percent = (value) => `${(Number(value || 0) * 100).toFixed(2)}%`;
const timestamp = (value) => (value ? new Date(value).toLocaleString() : '—');
const readableDate = (value) => (value ? new Date(`${dateOnly(value)}T00:00:00Z`).toLocaleDateString(undefined, {
  day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC',
}) : '—');

const statusStyle = {
  queued: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  running: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300',
  completed: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  failed: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300',
  cancelled: 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
};

const StatusBadge = ({ status }) => (
  <span className={`rounded-full px-2.5 py-1 text-xs font-bold uppercase tracking-wide ${statusStyle[status] || statusStyle.cancelled}`}>
    {status}
  </span>
);

const PremiumLocked = () => (
  <section className={`${card} mx-auto max-w-2xl text-center`} data-testid="premium-lock">
    <FaCrown className="mx-auto text-4xl text-amber-500" aria-hidden="true" />
    <p className="mt-4 text-sm font-bold uppercase tracking-[0.2em] text-amber-700 dark:text-amber-300">Premium feature</p>
    <h1 className="mt-2 text-3xl font-black">Backtesting is locked</h1>
    <p className="mx-auto mt-3 max-w-xl text-slate-600 dark:text-slate-300">
      A Backtest asks, “How would this strategy have behaved against historical market data?” Premium includes repeatable simulations using a recorded dataset and strategy version.
      Existing results remain safely retained if a subscription changes.
    </p>
    <Link to="/subscribe" className="mt-6 inline-flex rounded-xl bg-blue-600 px-5 py-3 font-bold text-white hover:bg-blue-700">
      See plans with Backtesting
    </Link>
  </section>
);

const Metric = ({ label, value, help }) => (
  <div className="rounded-xl bg-slate-50 p-4 dark:bg-slate-950">
    <p className="text-xs font-bold uppercase tracking-wide text-slate-500" title={help}>{label}</p>
    <p className="mt-1 text-xl font-black">{value}</p>
    {help && <p className="mt-1 text-xs text-slate-500">{help}</p>}
  </div>
);

const LineChart = ({ values, accessor, label, tone = 'blue' }) => {
  const points = chartPoints(values, accessor);
  const stroke = tone === 'red' ? '#dc2626' : '#2563eb';
  return (
    <figure className="mt-4">
      <figcaption className="mb-2 text-sm font-bold">{label}</figcaption>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-950">
        {points ? (
          <svg viewBox="0 0 720 220" className="h-52 w-full" role="img" aria-label={label} preserveAspectRatio="none">
            <polyline points={points} fill="none" stroke={stroke} strokeWidth="3" vectorEffect="non-scaling-stroke" />
          </svg>
        ) : <p className="p-8 text-center text-sm text-slate-500">No chart data is available.</p>}
      </div>
    </figure>
  );
};

const BacktestForm = ({ options, onCreate, creating, createFeedback, requestedBotId = '' }) => {
  const { axiosInstance } = useContext(AuthContext);
  const [botId, setBotId] = useState('');
  const [snapshotSymbol, setSnapshotSymbol] = useState('');
  const [snapshotKey, setSnapshotKey] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [assumptions, setAssumptions] = useState(null);
  const [errors, setErrors] = useState({});
  const [datasetPickerOpen, setDatasetPickerOpen] = useState(false);
  const initialSelectionApplied = useRef(false);
  const initializedRangeKey = useRef('');
  const selectedSnapshotScope = useRef('');
  const manuallySelectedSnapshot = useRef(false);
  const datasetPickerTrigger = useRef(null);
  const datasetPickerPanel = useRef(null);
  const bots = useMemo(() => options?.bots || [], [options]);
  const incompatibleBots = options?.incompatible_bots || [];
  const selectedBot = bots.find((item) => String(item.id) === String(botId));
  const selectedBotId = selectedBot?.id;
  const selectedBotDirection = selectedBot?.position_direction || 'LONG';
  const normalizedSnapshotSymbol = snapshotSymbol.trim().toUpperCase();
  const debouncedSnapshotSymbol = useDebouncedValue(normalizedSnapshotSymbol);
  const snapshotSymbolValid = /^[A-Z0-9][A-Z0-9.-]{0,63}$/.test(normalizedSnapshotSymbol);
  const scopedSymbolReady = snapshotSymbolValid
    && debouncedSnapshotSymbol === normalizedSnapshotSymbol;
  const scopedOptionsQuery = useQuery({
    queryKey: ['durable-backtest-options', selectedBotId, debouncedSnapshotSymbol],
    queryFn: async ({ signal }) => (
      await axiosInstance.get('/api/backtests/options', {
        params: { bot_id: selectedBotId, symbol: debouncedSnapshotSymbol },
        signal,
      })
    ).data,
    enabled: Boolean(selectedBotId) && scopedSymbolReady,
    retry: 1,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });
  const scopedOptions = selectedBotId && scopedSymbolReady
    ? scopedOptionsQuery.data
    : null;
  const snapshots = useMemo(() => scopedOptions?.snapshots || [], [scopedOptions]);
  const snapshot = snapshots.find((item) => backtestSnapshotKey(item) === snapshotKey);
  const latestSnapshot = snapshots[0] || null;
  const eligibleRange = eligibleBacktestRange(snapshot);
  const rangeOutsideEligible = isBacktestRangeOutsideEligible({
    snapshot, startDate, endDate,
  });

  useEffect(() => {
    if (!options || initialSelectionApplied.current) return;
    initialSelectionApplied.current = true;
    setBotId(ownedPreselectedBacktestBotId(requestedBotId, bots));
  }, [bots, options, requestedBotId]);

  const changeBot = (nextBotId) => {
    initializedRangeKey.current = '';
    selectedSnapshotScope.current = '';
    manuallySelectedSnapshot.current = false;
    setBotId(nextBotId);
    setSnapshotSymbol('');
    setSnapshotKey('');
    setStartDate('');
    setEndDate('');
    setDatasetPickerOpen(false);
    setErrors({});
  };

  const changeSnapshotSymbol = (value) => {
    initializedRangeKey.current = '';
    selectedSnapshotScope.current = '';
    manuallySelectedSnapshot.current = false;
    setSnapshotSymbol(value.toUpperCase());
    setSnapshotKey('');
    setStartDate('');
    setEndDate('');
    setDatasetPickerOpen(false);
    setErrors({});
  };

  const changeSnapshot = (nextSnapshotKey, { manual = true } = {}) => {
    initializedRangeKey.current = '';
    manuallySelectedSnapshot.current = manual;
    setSnapshotKey(nextSnapshotKey);
    setStartDate('');
    setEndDate('');
    setErrors({});
  };

  useEffect(() => {
    if (!selectedBotId || !scopedSymbolReady || !scopedOptionsQuery.isSuccess) return;
    if (!snapshots.length) {
      manuallySelectedSnapshot.current = false;
      selectedSnapshotScope.current = '';
      if (snapshotKey) {
        initializedRangeKey.current = '';
        setSnapshotKey('');
        setStartDate('');
        setEndDate('');
      }
      return;
    }
    const scope = `${selectedBotId}:${normalizedSnapshotSymbol}`;
    const selected = snapshots.find((item) => backtestSnapshotKey(item) === snapshotKey);
    const next = selectBacktestSnapshot({
      snapshots,
      selectedKey: snapshotKey,
      manual: manuallySelectedSnapshot.current,
    });
    const scopeChanged = selectedSnapshotScope.current !== scope;
    selectedSnapshotScope.current = scope;
    if (!next || (!scopeChanged && selected && manuallySelectedSnapshot.current)) return;
    const nextKey = backtestSnapshotKey(next);
    if (nextKey !== snapshotKey) {
      initializedRangeKey.current = '';
      manuallySelectedSnapshot.current = false;
      setSnapshotKey(nextKey);
      setStartDate('');
      setEndDate('');
      setErrors({});
    }
  }, [normalizedSnapshotSymbol, scopedOptionsQuery.isSuccess, scopedSymbolReady, selectedBotId, snapshotKey, snapshots]);

  useEffect(() => {
    if (!datasetPickerOpen) return undefined;
    const closeOnEscape = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      setDatasetPickerOpen(false);
      window.requestAnimationFrame(() => datasetPickerTrigger.current?.focus());
    };
    window.requestAnimationFrame(() => datasetPickerPanel.current?.focus());
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [datasetPickerOpen]);

  const closeDatasetPicker = () => {
    setDatasetPickerOpen(false);
    window.requestAnimationFrame(() => datasetPickerTrigger.current?.focus());
  };

  const chooseDataset = (item) => {
    changeSnapshot(backtestSnapshotKey(item));
    closeDatasetPicker();
  };

  useEffect(() => {
    if (!assumptions && options?.execution_profile) {
      setAssumptions({ ...options.execution_profile });
    }
  }, [assumptions, options]);

  useEffect(() => {
    if (!selectedBotId) return;
    const profile = scopedOptions?.execution_profiles?.[selectedBotDirection]
      || options?.execution_profiles?.[selectedBotDirection]
      || options?.execution_profile;
    if (profile) setAssumptions({ ...profile });
  }, [selectedBotDirection, selectedBotId, scopedOptions, options]);

  useEffect(() => {
    if (!snapshot) return;
    const rangeKey = [
      selectedBotId,
      snapshot.snapshot_id,
      snapshot.series_id,
      eligibleRange.start,
      eligibleRange.end,
    ].join(':');
    if (initializedRangeKey.current === rangeKey) return;
    initializedRangeKey.current = rangeKey;
    setStartDate(eligibleRange.start);
    setEndDate(eligibleRange.end);
    setErrors({});
  }, [eligibleRange.end, eligibleRange.start, selectedBotId, snapshot]);

  if (!assumptions) return <LoadingSpinner label="Loading simulation assumptions…" showLabel />;

  const updateAssumption = (field, value) => setAssumptions((current) => ({ ...current, [field]: value }));
  const useAvailableDateRange = () => {
    setStartDate(eligibleRange.start);
    setEndDate(eligibleRange.end);
    setErrors((current) => {
      const next = { ...current };
      delete next.range;
      return next;
    });
  };
  const submit = (event) => {
    event.preventDefault();
    const nextErrors = validateBacktestDraft({
      botId, bot: selectedBot, snapshot, startDate, endDate, assumptions,
    });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    onCreate({ botId, snapshot, startDate, endDate, assumptions });
  };

  return (
    <form onSubmit={submit} className={card} noValidate>
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-bold uppercase tracking-wide text-blue-600">Premium backtest</p>
          <h2 className="mt-1 text-2xl font-black">Create a Backtest</h2>
          <p className="mt-2 max-w-xl text-sm text-slate-500">Choose a saved bot and historical period. This does not run the bot today or create any real or simulated broker order.</p>
        </div>
        <FaFlask className="text-2xl text-blue-600" aria-hidden="true" />
      </div>
      <div className="mt-6 grid gap-5 md:grid-cols-2">
        <label className="text-sm font-semibold">
          Bot and strategy
          <select value={botId} onChange={(event) => changeBot(event.target.value)} className={input}>
            <option value="">Choose a bot</option>
            {bots.map((bot) => <option key={bot.id} value={bot.id}>{bot.name} · {strategyDisplayName(bot.strategy_family, bot.strategy_name)}</option>)}
          </select>
          {errors.botId && <span className="mt-1 block text-xs text-red-600">{errors.botId}</span>}
        </label>
        <label className="text-sm font-semibold">
          Historical stock symbol
          <input
            type="text"
            value={snapshotSymbol}
            onChange={(event) => changeSnapshotSymbol(event.target.value)}
            className={input}
            placeholder="For example, AAPL"
            autoComplete="off"
            spellCheck="false"
            disabled={!selectedBotId}
            data-testid="backtest-symbol-search"
          />
          <span className="mt-1 block text-xs font-normal text-slate-500">
            Choose any stock with eligible historical data. This can differ from the stocks your bot watches in Practice.
          </span>
          {snapshotSymbol && !snapshotSymbolValid && (
            <span className="mt-1 block text-xs text-red-600">Use letters, numbers, dots, or hyphens.</span>
          )}
        </label>
        <section className="text-sm font-semibold md:col-span-2" aria-labelledby="backtest-dataset-heading">
          <h3 id="backtest-dataset-heading">Historical dataset</h3>
          {snapshot && (
            <div className="mt-1 rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-950" data-testid="backtest-dataset-summary" data-snapshot-key={backtestSnapshotKey(snapshot)}>
              <p className="font-semibold text-slate-700 dark:text-slate-200">
                {backtestSnapshotKey(snapshot) === backtestSnapshotKey(latestSnapshot)
                  ? 'Latest compatible historical dataset selected automatically.'
                  : 'Older recorded historical dataset selected.'}
              </p>
              <div className="mt-3 grid gap-1 text-sm font-normal text-slate-600 dark:text-slate-300 sm:grid-cols-3">
                <p><strong className="text-slate-900 dark:text-white">{snapshot.symbol}</strong></p>
                <p>{readableDate(snapshot.raw_range_start || snapshot.range_start)} – {readableDate(snapshot.raw_range_end || snapshot.range_end)}</p>
                <p>Finalized {readableDate(snapshot.finalized_at)}</p>
              </div>
              <button
                ref={datasetPickerTrigger}
                type="button"
                onClick={() => setDatasetPickerOpen(true)}
                className="mt-4 rounded-lg border border-slate-300 px-3 py-2 font-bold text-slate-800 hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 dark:border-slate-600 dark:text-slate-100 dark:hover:bg-slate-900"
                data-testid="backtest-change-dataset"
              >
                Change dataset
              </button>
              <details className="mt-3 text-xs font-normal text-slate-500">
                <summary className="cursor-pointer font-semibold text-slate-600 dark:text-slate-300">Technical details</summary>
                <p className="mt-2 break-all">Recorded dataset identity: {snapshot.snapshot_id} · series {snapshot.series_id}</p>
              </details>
            </div>
          )}
          {!normalizedSnapshotSymbol && selectedBotId && (
            <span role="status" className="mt-1 block text-xs font-normal text-slate-500">Enter a stock symbol to see available historical datasets.</span>
          )}
          {snapshotSymbolValid && scopedOptionsQuery.isLoading && (
            <span role="status" aria-live="polite" className="mt-1 block text-xs font-normal text-slate-500">Checking historical data…</span>
          )}
          {snapshotSymbolValid && scopedOptionsQuery.isSuccess && snapshots.length === 0 && (
            <span role="status" aria-live="polite" className="mt-1 block text-xs font-normal text-slate-500" data-testid="backtest-snapshot-unavailability">
              {snapshotUnavailabilityMessage(
                scopedOptions?.snapshot_unavailability,
                normalizedSnapshotSymbol,
              )}
            </span>
          )}
          {scopedOptionsQuery.isError && (
            <span role="alert" className="mt-1 block text-xs text-red-600">{backtestErrorMessage(scopedOptionsQuery.error)}</span>
          )}
          {errors.snapshot && <span className="mt-1 block text-xs text-red-600">{errors.snapshot}</span>}
          {datasetPickerOpen && snapshot && (
            <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/50 p-4 sm:items-center" role="presentation">
              <section
                ref={datasetPickerPanel}
                role="dialog"
                aria-modal="true"
                aria-labelledby="backtest-change-dataset-title"
                tabIndex="-1"
                className="max-h-[min(42rem,calc(100vh-2rem))] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-5 shadow-2xl outline-none dark:bg-slate-900"
                data-testid="backtest-dataset-drawer"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wide text-blue-600">Advanced choice</p>
                    <h4 id="backtest-change-dataset-title" className="mt-1 text-xl font-black">Recorded historical datasets</h4>
                    <p className="mt-1 text-sm font-normal text-slate-600 dark:text-slate-300">Choose an older immutable dataset only when you need to reproduce a specific recorded input.</p>
                  </div>
                  <button type="button" onClick={closeDatasetPicker} className="rounded-lg px-3 py-2 font-bold hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 dark:hover:bg-slate-800">Close</button>
                </div>
                <div className="mt-5 space-y-2">
                  {snapshots.map((item) => {
                    const itemKey = backtestSnapshotKey(item);
                    const current = itemKey === snapshotKey;
                    return (
                      <button
                        key={itemKey}
                        type="button"
                        onClick={() => chooseDataset(item)}
                        data-testid="backtest-dataset-option"
                        data-snapshot-key={itemKey}
                        className={`w-full rounded-xl border p-4 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 ${current ? 'border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-950/40' : 'border-slate-200 hover:border-blue-400 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800'}`}
                      >
                        <span className="flex flex-wrap items-center justify-between gap-2 font-bold"><span>{item.symbol}</span>{current && <span className="rounded-full bg-blue-600 px-2 py-0.5 text-xs text-white">Current</span>}</span>
                        <span className="mt-1 block text-sm font-normal text-slate-600 dark:text-slate-300">{readableDate(item.raw_range_start || item.range_start)} – {readableDate(item.raw_range_end || item.range_end)} · finalized {readableDate(item.finalized_at)}</span>
                        {(item.provider || item.feed) && <span className="mt-1 block text-xs font-normal text-slate-500">{item.provider}/{item.feed}</span>}
                      </button>
                    );
                  })}
                </div>
              </section>
            </div>
          )}
        </section>
        <label className="text-sm font-semibold">
          Start date
          <input type="date" value={startDate} min={eligibleRange.start} max={eligibleRange.end} onChange={(event) => setStartDate(event.target.value)} className={input} aria-describedby={snapshot?.eligible_start_reason ? 'eligible-start-explanation' : undefined} data-testid="backtest-start-date" disabled={!snapshot} />
        </label>
        <label className="text-sm font-semibold">
          End date
          <input type="date" value={endDate} min={eligibleRange.start} max={eligibleRange.end} onChange={(event) => setEndDate(event.target.value)} className={input} data-testid="backtest-end-date" disabled={!snapshot} />
        </label>
        {snapshot?.eligible_start_reason && dateOnly(snapshot.raw_range_start || snapshot.range_start) !== eligibleRange.start && (
          <p id="eligible-start-explanation" role="note" className="md:col-span-2 rounded-xl bg-blue-50 p-3 text-sm text-blue-950 dark:bg-blue-950/40 dark:text-blue-100" data-testid="eligible-start-explanation">
            Historical data is available from {readableDate(snapshot.raw_range_start || snapshot.range_start)}. This strategy needs earlier data for its indicators, so the first valid simulation date is {readableDate(eligibleRange.start)}.
          </p>
        )}
        {errors.range && <p role="alert" className="md:col-span-2 text-sm text-red-600">{errors.range}</p>}
        {rangeOutsideEligible && (
          <div className="md:col-span-2 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100" data-testid="backtest-range-correction">
            <span>Valid simulation dates are {readableDate(eligibleRange.start)} to {readableDate(eligibleRange.end)}.</span>
            <button type="button" onClick={useAvailableDateRange} className="rounded-lg border border-amber-400 px-3 py-2 font-bold hover:bg-amber-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 dark:border-amber-700 dark:hover:bg-amber-950">
              Use available date range
            </button>
          </div>
        )}
        {incompatibleBots.length > 0 && (
          <div className="md:col-span-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
            <strong>{incompatibleBots.length} saved bot{incompatibleBots.length === 1 ? '' : 's'} cannot be tested yet.</strong>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              {incompatibleBots.map((bot) => <li key={bot.id}>{bot.name}: {bot.reason}</li>)}
            </ul>
          </div>
        )}
        <label className="text-sm font-semibold">
          Initial capital (USD)
          <input type="number" min="1" step="100" value={assumptions.initial_capital} onChange={(event) => updateAssumption('initial_capital', event.target.value)} className={input} />
          {errors.initialCapital && <span className="mt-1 block text-xs text-red-600">{errors.initialCapital}</span>}
        </label>
        <label className="text-sm font-semibold">
          Allocation per position (%)
          <input type="number" min="0.01" max="100" step="0.01" value={assumptions.allocation_percent} onChange={(event) => updateAssumption('allocation_percent', event.target.value)} className={input} />
          {errors.allocation && <span className="mt-1 block text-xs text-red-600">{errors.allocation}</span>}
        </label>
        <label className="text-sm font-semibold">
          Commission per share (USD)
          <input type="number" min="0" step="0.0001" value={assumptions.commission_per_share} onChange={(event) => updateAssumption('commission_per_share', event.target.value)} className={input} />
          {errors.commission && <span className="mt-1 block text-xs text-red-600">{errors.commission}</span>}
        </label>
        <label className="text-sm font-semibold">
          Slippage (basis points)
          <input type="number" min="0" step="0.01" value={assumptions.slippage_bps} onChange={(event) => updateAssumption('slippage_bps', event.target.value)} className={input} />
          {errors.slippage && <span className="mt-1 block text-xs text-red-600">{errors.slippage}</span>}
        </label>
      </div>

      {(selectedBot || snapshot) && (
        <div className="mt-6 grid gap-3 rounded-xl bg-slate-50 p-4 text-sm dark:bg-slate-950 sm:grid-cols-2">
          {selectedBot && <p data-testid="backtest-strategy-summary"><strong>Strategy:</strong> <span>{strategyDisplayName(selectedBot.strategy_family, selectedBot.strategy_name)}</span> · {selectedBot.position_direction || 'LONG'} · version {selectedBot.strategy_schema_version}</p>}
          {selectedBot && <p data-testid="backtest-stock-focus-summary"><strong>{selectedBot.stock_selection_mode === 'ALL_STOCKS' ? 'Find stocks:' : 'Stock Focus:'}</strong> <span>{selectedBot.stock_selection_mode === 'ALL_STOCKS' ? 'All Stocks — chosen directly by you' : selectedBot.stock_focus_name}</span></p>}
          {selectedBot && <p><strong>Strategy version:</strong> {selectedBot.configuration_hash.slice(0, 12)}…</p>}
          {snapshot && <p><strong>Data:</strong> {snapshot.provider}/{snapshot.feed}, {snapshot.timeframe}, {snapshot.adjustment_basis}</p>}
          {snapshot && <p><strong>Data coverage:</strong> {dateOnly(snapshot.raw_range_start || snapshot.range_start)} to {dateOnly(snapshot.raw_range_end || snapshot.range_end)} · {snapshot.selected_row_count} rows</p>}
          {snapshot && <p><strong>Valid simulation dates:</strong> {eligibleRange.start} to {eligibleRange.end}</p>}
        </div>
      )}

      <div className="mt-6 rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm text-blue-950 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-100">
        <strong>How this test works:</strong> signals use completed daily historical data and simulated fills use the next session&apos;s open. {selectedBot?.position_direction === 'SHORT' ? 'This is a fully collateralized SHORT simulation: sale proceeds remain restricted, there is no leverage, and it holds at most one short position.' : 'It is long-only, cash-only, and holds at most one position.'} Technical assumptions are available in the saved result.
        <span className="mt-2 block">This tests one selected stock. It does not represent the performance of the whole bot.</span>
      </div>
      {selectedBot?.position_direction === 'SHORT' && <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm leading-6 text-rose-950 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-100"><strong>Results are a deterministic Practice simulation.</strong> Short results are price-based and do not model all real-world borrowing, financing, dividend, locate, or margin effects. Past simulated performance does not guarantee future results.</div>}
      <button type="submit" disabled={creating || !bots.length || !selectedBotId || scopedOptionsQuery.isLoading || !snapshots.length} aria-busy={creating} className="mt-6 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-400">
        {creating ? <LoadingSpinner size="sm" tone="light" label="Creating backtest…" /> : <FaPlay />}
        {creating ? 'Creating…' : 'Create Backtest'}
      </button>
      {creating && <p role="status" className="mt-2 text-sm text-blue-700 dark:text-blue-300">Starting your historical test…</p>}
      {createFeedback && <ActionFeedback feedback={createFeedback} />}
      {!bots.length && (
        <p className="mt-3 text-sm text-amber-700 dark:text-amber-300">
          Create a bot before starting a Backtest.
        </p>
      )}
    </form>
  );
};

const JobHistory = ({ jobs, loading }) => (
  <section className={card}>
    <h2 className="text-xl font-black">Your Backtests</h2>
    <p className="mt-1 text-sm text-slate-500">Tests are saved, so progress and results remain available across refreshes and service restarts.</p>
    {loading ? <div className="mt-6"><LoadingSpinner label="Loading backtests…" showLabel /></div> : jobs.length === 0 ? (
      <div className="mt-6 rounded-xl border border-dashed border-slate-300 p-8 text-center text-slate-500 dark:border-slate-700">No Backtests yet.</div>
    ) : (
      <div className="mt-5 space-y-3">
        {jobs.map((job) => (
          <Link key={job.id} to={`/backtests/${job.id}`} className="block rounded-xl border border-slate-200 p-4 transition hover:border-blue-400 hover:bg-blue-50/40 dark:border-slate-800 dark:hover:border-blue-700 dark:hover:bg-blue-950/20">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-bold">{job.bot_name || 'Bot'} · {job.symbol}</p>
                <p className="text-xs text-slate-500">{dateOnly(job.start_date)} to {dateOnly(job.end_date)} · created {timestamp(job.created_at)}</p>
              </div>
              <StatusBadge status={job.status} />
            </div>
            <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800" aria-label={`${Number(job.progress || 0).toFixed(0)} percent complete`}>
              <div className="h-full bg-blue-600" style={{ width: `${Math.max(0, Math.min(100, Number(job.progress || 0)))}%` }} />
            </div>
          </Link>
        ))}
      </div>
    )}
  </section>
);

const BacktestResult = ({ result, verification, verifying, verifyFeedback, onVerify }) => {
  const presented = presentBacktestResult(result);
  const decisions = summarizeDecisions(presented.decision_lineage);
  const isShort = presented.is_short;
  const assumptions = presented.execution_assumptions || {};
  const commissionAssumption = assumptions.commission_per_share === undefined
    ? '—'
    : `${money(assumptions.commission_per_share)} per share`;
  const slippageAssumption = assumptions.slippage_bps === undefined
    ? '—'
    : `${Number(assumptions.slippage_bps).toLocaleString()} basis points`;
  return (
    <section className="space-y-6">
      <div className={card}>
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-2xl font-black">{strategyDisplayName(presented.strategy_family, presented.strategy_name || 'Backtest result')}</h2>
          {isShort && <span className="rounded-full bg-rose-100 px-3 py-1 text-xs font-black tracking-wide text-rose-800 dark:bg-rose-950 dark:text-rose-200">SHORT STRATEGY</span>}
        </div>
        {isShort && <p className="mt-2 text-sm text-slate-500">Short strategy return is shown alongside Market buy-and-hold return for market context.</p>}
        <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300" data-testid="historical-simulation-disclosure">
          This shows how these rules behaved for <strong>{presented.symbol}</strong> from <strong>{dateOnly(presented.start_date)}</strong> to <strong>{dateOnly(presented.end_date)}</strong>, using recorded historical data and the displayed assumptions. It is not a prediction, recommendation, or promise of future results. Results for one stock do not represent the performance of the whole bot.
        </p>
        <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300" data-testid="backtest-manual-decision-disclosure">
          Backtests simulate the strategy&apos;s rules and assume the generated actions are followed. Manual changes to Practice approvals, including partial exits, are not included.
        </p>
      </div>
      <div className={`${card} grid gap-3 sm:grid-cols-2 lg:grid-cols-4`}>
        <Metric label="Initial equity" value={money(presented.initial_equity)} />
        <Metric label="Final equity" value={money(presented.final_equity)} />
        <Metric label={presented.strategy_return_label} value={percent(presented.total_return)} />
        <Metric label={presented.benchmark_label} value={percent(presented.benchmark_return)} help="Buy-and-hold market context for the same symbol and period; it is not a short-direction benchmark." />
        <Metric label="Realized P&L" value={money(presented.realized_pnl)} />
        <Metric label="Unrealized P&L" value={money(presented.unrealized_pnl)} />
        <Metric label="Maximum drawdown" value={percent(presented.max_drawdown)} help="Largest peak-to-trough decline in simulated equity." />
        <Metric label="Drawdown duration" value={`${presented.max_drawdown_duration} bars`} />
        <Metric label={isShort ? 'Completed short trades' : 'Trades'} value={presented.trade_count} />
        <Metric label="Wins / losses" value={`${presented.win_count} / ${presented.loss_count}`} />
        <Metric label="Win rate" value={percent(presented.win_rate)} help="Share of closed trades with positive realized P&L." />
        <Metric label="Average P&L per completed trade" value={money(presented.expectancy)} help="Average realized gain or loss across completed simulated trades." />
        <Metric label="Time invested" value={percent(presented.exposure)} help="Share of the historical period during which the strategy held a simulated position." />
        <Metric label="Commission assumption" value={commissionAssumption} />
        <Metric label="Slippage assumption" value={slippageAssumption} />
      </div>
      {isShort && presented.open_position && <div className={`${card} border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30`}><p className="font-black">OPEN SHORT POSITION AT END</p><p className="mt-2 text-sm">{presented.open_position.quantity} virtual shares remained short at {tradePrice(presented.open_position_mark_price)} with unrealized P&amp;L of {money(presented.unrealized_pnl)}. Pinngro did not fabricate a cover.</p></div>}
      <div className={card}>
        <h2 className="text-xl font-black">Performance</h2>
        <LineChart values={presented.equity_curve} accessor={(row) => row.equity} label="Strategy equity curve" />
        <LineChart values={presented.equity_curve} accessor={(row) => row.drawdown} label="Drawdown over time" tone="red" />
        <p className="mt-3 text-xs text-slate-500">Benchmark comparison is shown as the persisted period return because this deterministic result version does not store a separate benchmark curve.</p>
      </div>
      <div className={card}>
        <h2 className="text-xl font-black">Trades and decisions</h2>
        <p className="mt-2 text-sm text-slate-500">Decision summary: {Object.entries(decisions).map(([key, count]) => `${key} ${count}`).join(' · ') || 'No evaluated decisions'}</p>
        <div className="mt-4 space-y-3 md:hidden">
          {presented.trades.map((trade, index) => (
            <article key={`mobile-${trade.entry_at}-${trade.exit_at}-${index}`} className="rounded-xl border border-slate-200 p-4 dark:border-slate-800">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <p className="min-w-0"><span className="block font-bold">{trade.presentation_entry_label}</span><span className="break-words text-slate-500">{timestamp(trade.entry_at)}</span></p>
                <p className="min-w-0"><span className="block font-bold">{trade.presentation_exit_label}</span><span className="break-words text-slate-500">{timestamp(trade.exit_at)}</span></p>
                <p><span className="block text-xs font-bold uppercase text-slate-500">{isShort ? 'Short entry price' : 'Entry price'}</span>{tradePrice(trade.presentation_entry_price)}</p>
                <p><span className="block text-xs font-bold uppercase text-slate-500">{isShort ? 'Cover price' : 'Exit price'}</span>{tradePrice(trade.presentation_exit_price)}</p>
                <p><span className="block text-xs font-bold uppercase text-slate-500">Quantity</span>{trade.quantity}</p>
                <p><span className="block text-xs font-bold uppercase text-slate-500">P&amp;L</span>{money(trade.realized_pnl)}</p>
              </div>
              <p className="mt-3 break-words text-sm text-slate-500">{trade.exit_reason || 'Signal'}</p>
            </article>
          ))}
        </div>
        <div className="mt-4 hidden overflow-x-auto md:block">
          <table className="min-w-full text-left text-sm">
            <thead><tr className="border-b border-slate-200 dark:border-slate-800"><th className="p-3">{presented.entry_label}</th><th className="p-3">{presented.exit_label}</th><th className="p-3">Quantity</th><th className="p-3">{isShort ? 'Short entry price' : 'Entry price'}</th><th className="p-3">{isShort ? 'Cover price' : 'Exit price'}</th><th className="p-3">P&L</th><th className="p-3">Reason</th></tr></thead>
            <tbody>{presented.trades.map((trade, index) => (
              <tr key={`${trade.entry_at}-${trade.exit_at}-${index}`} className="border-b border-slate-100 dark:border-slate-800">
                <td className="p-3"><span className="font-bold">{trade.presentation_entry_label}</span><br />{timestamp(trade.entry_at)}</td><td className="p-3"><span className="font-bold">{trade.presentation_exit_label}</span><br />{timestamp(trade.exit_at)}</td><td className="p-3">{trade.quantity}</td><td className="p-3">{tradePrice(trade.presentation_entry_price)}</td><td className="p-3">{tradePrice(trade.presentation_exit_price)}</td><td className="p-3">{money(trade.realized_pnl)}</td><td className="p-3">{trade.exit_reason || 'Signal'}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
        {!presented.trades.length && <p className="p-6 text-center text-slate-500">No completed trades in this simulation.</p>}
      </div>
      <div className={card}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-black">Reproducibility</h2>
            <p className="mt-1 max-w-2xl text-sm text-slate-500">Verification reruns the Backtest with the same recorded strategy, market data, and assumptions. It proves repeatability, not future performance.</p>
          </div>
          <button type="button" onClick={onVerify} disabled={verifying} className="rounded-xl bg-slate-900 px-4 py-2.5 font-bold text-white hover:bg-slate-700 disabled:bg-slate-400 dark:bg-white dark:text-slate-950">
            {verifying ? 'Verifying…' : 'Verify this result'}
          </button>
        </div>
        {verifying && <p role="status" className="mt-2 text-sm text-blue-700 dark:text-blue-300">Checking this saved result…</p>}
        {verifyFeedback && <ActionFeedback feedback={verifyFeedback} />}
        {verification && (
          <div className={`mt-4 flex items-center gap-3 rounded-xl p-4 ${verification.exact_match ? 'bg-emerald-50 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200' : 'bg-red-50 text-red-900 dark:bg-red-950/40 dark:text-red-200'}`}>
            {verification.exact_match ? <FaCheckCircle /> : <FaBan />}
            <span className="font-bold">{verification.exact_match ? 'Verified exact match' : 'Verification mismatch'}</span>
          </div>
        )}
        <details className="mt-4 text-sm text-slate-500"><summary className="cursor-pointer font-semibold">Technical lineage</summary><div className="mt-2 break-all"><p>Result hash: {presented.result_hash}</p><p>Snapshot: {presented.market_snapshot_id}</p><p>Strategy: {presented.strategy_hash}</p><p>Evaluator: {presented.evaluator_version}</p><p>Execution profile: {presented.execution_profile_version || presented.execution_assumptions_hash}</p></div></details>
      </div>
    </section>
  );
};

const JobDetail = ({ jobId, job, result, loading, error, onCancel, cancelling, cancelFeedback, onVerify, verifying, verifyFeedback, verification }) => {
  if (loading) return <LoadingSpinner label="Loading Backtest…" showLabel />;
  if (error || !job) return <div className={`${card} text-red-700 dark:text-red-300`}>{backtestErrorMessage(error)}</div>;
  return (
    <div className="space-y-6">
      <section className={card}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div><p className="text-sm font-bold uppercase tracking-wide text-blue-600">{job.symbol} · {job.bot_name || 'Backtest'}</p><h1 className="mt-1 text-3xl font-black">Backtest status</h1><p className="mt-2 text-sm text-slate-500">{dateOnly(job.start_date)} to {dateOnly(job.end_date)} · {job.provider}/{job.feed} · {job.timeframe}/{job.adjustment_basis}</p></div>
          <StatusBadge status={job.status} />
        </div>
        <div className="mt-5 h-3 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800"><div className="h-full bg-blue-600 transition-all" style={{ width: `${Number(job.progress || 0)}%` }} /></div>
        <div className="mt-4 grid gap-3 text-sm sm:grid-cols-3"><p><FaClock className="mr-2 inline" />Created {timestamp(job.created_at)}</p><p>Started {timestamp(job.started_at)}</p><p>Completed {timestamp(job.completed_at)}</p></div>
        {job.error_message && <p className="mt-4 rounded-xl bg-red-50 p-4 text-sm text-red-800 dark:bg-red-950/40 dark:text-red-200">The backtest failed safely. Review the selected snapshot and strategy, then try again.</p>}
        {['queued', 'running'].includes(job.status) && <button type="button" onClick={onCancel} disabled={cancelling} className="mt-5 inline-flex items-center gap-2 rounded-xl border border-red-300 px-4 py-2.5 font-bold text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-red-900 dark:text-red-300"><FaStop />{cancelling ? 'Cancelling…' : 'Cancel backtest'}</button>}
        {cancelling && <p role="status" className="mt-2 text-sm text-blue-700 dark:text-blue-300">Sending cancellation request…</p>}
        {cancelFeedback && <ActionFeedback feedback={cancelFeedback} />}
      </section>
      {job.status === 'completed' && result && <BacktestResult result={result} verification={verification} verifying={verifying} verifyFeedback={verifyFeedback} onVerify={onVerify} />}
      {job.status === 'completed' && !result && <div className={card}><LoadingSpinner label="Loading immutable result…" showLabel /></div>}
      <p className="text-xs text-slate-500">Job reference: {jobId}</p>
    </div>
  );
};

const ActionFeedback = ({ feedback }) => (
  <p role={feedback.type === 'error' ? 'alert' : 'status'} className={`mt-2 rounded-xl p-3 text-sm ${feedback.type === 'error' ? 'bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-200' : 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200'}`}>
    {feedback.message}
  </p>
);

const BacktestsPage = () => {
  const { jobId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { axiosInstance } = useContext(AuthContext);
  const billingQuery = usePremiumEntitlement();
  const premium = isPremiumPlan(billingQuery.data);
  const [createFeedback, setCreateFeedback] = useState(null);
  const [cancelFeedback, setCancelFeedback] = useState(null);
  const [verifyFeedback, setVerifyFeedback] = useState(null);
  const actionInFlight = useRef({ create: false, cancel: false, verify: false });
  const [verification, setVerification] = useState(null);
  const requestedBotId = searchParams.get('bot_id') || '';
  const handoffInstrumentId = searchParams.get('research_instrument_id');
  const handoffFamily = searchParams.get('research_family');
  const handoffArtifactId = searchParams.get('analysis_artifact_id');
  const handoffRequested = Boolean(handoffInstrumentId && handoffFamily && handoffArtifactId);
  const [mode, setMode] = useState(handoffRequested ? 'research' : 'quick');
  const researchCapabilitiesQuery = useQuery({
    queryKey: ['strategy-research-capabilities'],
    queryFn: async () => (await axiosInstance.get('/api/strategy-research/capabilities')).data,
    enabled: premium,
    retry: 1,
    staleTime: 30000,
  });
  const researchAvailable = researchCapabilitiesQuery.data?.available === true;
  const handoffQuery = useQuery({
    queryKey: ['stock-explorer-research-handoff', handoffInstrumentId, handoffArtifactId],
    queryFn: async () => (await axiosInstance.get(`/api/stock-explorer/instruments/${handoffInstrumentId}/research-capabilities`, { params: { analysis_artifact_id: handoffArtifactId } })).data,
    enabled: premium && researchAvailable && handoffRequested,
    retry: false,
  });
  const handoffFamilyAuthority = handoffQuery.data?.families?.find((item) => item.family === handoffFamily);
  const handoffReady = handoffRequested && handoffFamilyAuthority?.supported === true
    && Array.isArray(handoffFamilyAuthority?.compatible_definitions)
    && handoffFamilyAuthority.compatible_definitions.some((id) => researchCapabilitiesQuery.data?.strategies?.some((strategy) => strategy.id === id))
    && handoffQuery.data?.instrument_id === Number(handoffInstrumentId)
    && handoffQuery.data?.fit_artifact_id === handoffArtifactId
    && handoffQuery.data?.snapshot_id === researchCapabilitiesQuery.data?.snapshot?.id;
  const handoffPrefill = handoffReady ? { ...handoffQuery.data, family: handoffFamily,
    family_name: handoffFamilyAuthority.name,
    compatible_definitions: handoffFamilyAuthority.compatible_definitions } : null;

  const jobsQuery = useQuery({
    queryKey: ['durable-backtests'],
    queryFn: async () => (await axiosInstance.get('/api/backtests')).data.jobs || [],
    enabled: premium,
    retry: 1,
    refetchInterval: (query) => query.state.data?.some((job) => !TERMINAL_BACKTEST_STATES.has(job.status)) ? 3000 : false,
  });
  const optionsQuery = useQuery({
    queryKey: ['durable-backtest-options'],
    queryFn: async () => (await axiosInstance.get('/api/backtests/options')).data,
    enabled: premium && !jobId,
    retry: 1,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });
  const jobQuery = useQuery({
    queryKey: ['durable-backtest', jobId],
    queryFn: async () => (await axiosInstance.get(`/api/backtests/${jobId}`)).data,
    enabled: premium && Boolean(jobId),
    retry: 1,
    refetchInterval: (query) => query.state.data && !TERMINAL_BACKTEST_STATES.has(query.state.data.status) ? 2000 : false,
  });
  const resultQuery = useQuery({
    queryKey: ['durable-backtest-result', jobId],
    queryFn: async () => (await axiosInstance.get(`/api/backtests/${jobId}/result`)).data,
    enabled: premium && jobQuery.data?.status === 'completed',
    retry: 1,
  });

  const handleEntitlementError = (error) => {
    if (error?.response?.status === 403) {
      queryClient.invalidateQueries({ queryKey: ['billing-subscription'] });
    }
    return backtestErrorMessage(error);
  };
  const createMutation = useMutation({
    mutationFn: async (draft) => {
      const payload = buildBacktestRequest({ ...draft, idempotencyKey: crypto.randomUUID() });
      return (await axiosInstance.post('/api/backtests', payload)).data;
    },
    onSuccess: (job) => {
      actionInFlight.current.create = false;
      queryClient.invalidateQueries({ queryKey: ['durable-backtests'] });
      navigate(`/backtests/${job.id}`);
    },
    onError: (error) => {
      actionInFlight.current.create = false;
      setCreateFeedback({ type: 'error', message: `${handleEntitlementError(error)} Review your choices and try again.` });
    },
  });
  const cancelMutation = useMutation({
    mutationFn: async () => (await axiosInstance.post(`/api/backtests/${jobId}/cancel`)).data,
    onSuccess: (job) => {
      actionInFlight.current.cancel = false;
      queryClient.setQueryData(['durable-backtest', jobId], job);
      queryClient.invalidateQueries({ queryKey: ['durable-backtests'] });
      setCancelFeedback({ type: 'success', message: 'Backtest cancelled.' });
    },
    onError: (error) => {
      actionInFlight.current.cancel = false;
      setCancelFeedback({ type: 'error', message: `${handleEntitlementError(error)} You can try cancelling again.` });
    },
  });
  const verifyMutation = useMutation({
    mutationFn: async () => (await axiosInstance.post(`/api/backtests/${jobId}/verify`)).data,
    onSuccess: (result) => {
      actionInFlight.current.verify = false;
      setVerification(result);
      setVerifyFeedback(null);
    },
    onError: (error) => {
      actionInFlight.current.verify = false;
      setVerifyFeedback({ type: 'error', message: `${handleEntitlementError(error)} You can try verification again.` });
    },
  });

  const submitCreate = (draft) => {
    if (actionInFlight.current.create) return;
    actionInFlight.current.create = true;
    setCreateFeedback(null);
    createMutation.mutate(draft);
  };
  const submitCancel = () => {
    if (actionInFlight.current.cancel) return;
    actionInFlight.current.cancel = true;
    setCancelFeedback(null);
    cancelMutation.mutate();
  };
  const submitVerify = () => {
    if (actionInFlight.current.verify) return;
    actionInFlight.current.verify = true;
    setVerifyFeedback(null);
    verifyMutation.mutate();
  };

  const pageError = useMemo(() => (
    jobsQuery.error || optionsQuery.error ? backtestErrorMessage(jobsQuery.error || optionsQuery.error) : ''
  ), [jobsQuery.error, optionsQuery.error]);

  if (billingQuery.isLoading) return <><NavigationBar /><LoadingSpinner fullScreen label="Checking Premium access…" /></>;

  if (billingQuery.isError) {
    return (
      <div className="min-h-screen bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-white">
        <NavigationBar />
        <main className="mx-auto max-w-2xl px-4 pb-16 pt-28 sm:px-6">
          <section className={card}>
            <h1 className="text-2xl font-black">Premium access could not be verified</h1>
            <p className="mt-2 text-slate-600 dark:text-slate-300">No backtest details were loaded. Check your connection and try again.</p>
            <button type="button" onClick={() => billingQuery.refetch()} className="mt-5 rounded-xl bg-blue-600 px-4 py-2.5 font-bold text-white hover:bg-blue-700">Try again</button>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-white">
      <NavigationBar />
      <main className="mx-auto max-w-screen-xl px-4 pb-16 pt-24 sm:px-6">
        {!premium ? <PremiumLocked /> : (
          <>
            <header className="mb-8">
              {jobId && <Link to="/backtests" className="mb-4 inline-flex items-center gap-2 text-sm font-bold text-blue-700 hover:underline dark:text-blue-300"><FaArrowLeft />All backtests</Link>}
              <div className="flex flex-wrap items-end justify-between gap-4">
                <div><p className="text-sm font-bold uppercase tracking-[0.2em] text-blue-600">Premium · historical simulation</p><h1 className="mt-2 text-4xl font-black tracking-tight">Backtests</h1><p className="mt-2 max-w-3xl text-slate-600 dark:text-slate-300">A Backtest asks how a strategy would have behaved against recorded historical data. It is a learning tool, not a prediction of future results.</p></div>
                <div className="flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-2 text-xs font-bold text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"><FaShieldAlt />Historical simulation only</div>
              </div>
            </header>
            {!jobId && <div className="mb-6 flex gap-2 border-b border-slate-200 dark:border-slate-800" role="tablist" aria-label="Historical simulation mode">
              <button type="button" role="tab" aria-selected={mode === 'quick'} onClick={() => setMode('quick')} className={`border-b-2 px-4 py-3 font-bold ${mode === 'quick' ? 'border-blue-600 text-blue-700 dark:text-blue-300' : 'border-transparent text-slate-600 dark:text-slate-300'}`}>Quick Backtest</button>
              {researchAvailable && <button type="button" role="tab" aria-selected={mode === 'research'} onClick={() => setMode('research')} className={`border-b-2 px-4 py-3 font-bold ${mode === 'research' ? 'border-violet-700 text-violet-700 dark:text-violet-300' : 'border-transparent text-slate-600 dark:text-slate-300'}`}>Strategy Research · {researchCapabilitiesQuery.data?.access_mode === 'premium_open_beta' ? 'Open Beta' : 'Beta'}</button>}
            </div>}
            {handoffRequested && !jobId && !researchAvailable && <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm dark:border-amber-900 dark:bg-amber-950/30" role={researchCapabilitiesQuery.isError ? 'alert' : 'status'}>{researchCapabilitiesQuery.isPending ? 'Checking Strategy Research access for this stock…' : researchCapabilitiesQuery.isError ? 'Strategy Research access could not be checked.' : "Strategy Research doesn't currently support this stock/dataset or this account. The Fit finding remains historical context."}{researchCapabilitiesQuery.isError && <button type="button" onClick={() => researchCapabilitiesQuery.refetch()} disabled={researchCapabilitiesQuery.isFetching} className="ml-2 min-h-11 font-bold text-blue-700 underline disabled:opacity-50">{researchCapabilitiesQuery.isFetching ? 'Checking again…' : 'Retry access check'}</button>}</div>}
            {pageError && <div role="alert" className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">{pageError}</div>}
            {jobId ? (
              <JobDetail jobId={jobId} job={jobQuery.data} result={resultQuery.data} loading={jobQuery.isLoading} error={jobQuery.error} onCancel={submitCancel} cancelling={cancelMutation.isPending} cancelFeedback={cancelFeedback} onVerify={submitVerify} verifying={verifyMutation.isPending} verifyFeedback={verifyFeedback} verification={verification} />
            ) : mode === 'research' && researchAvailable ? (
              handoffRequested && handoffQuery.isPending ? <p className={card} role="status">Checking this exact stock, dataset, and Fit artifact before opening Research…</p>
                : handoffRequested && !handoffReady ? <div className={card} role={handoffQuery.isError ? 'alert' : 'status'}><p>{handoffQuery.isError ? 'Research support could not be verified.' : handoffFamilyAuthority?.reason || "Strategy Research doesn't currently support this stock/dataset."}</p><button type="button" onClick={() => handoffQuery.refetch()} disabled={handoffQuery.isFetching} className="mt-3 min-h-11 font-bold text-blue-700 underline disabled:opacity-50">{handoffQuery.isFetching ? 'Checking again…' : 'Retry Research support check'}</button></div>
                  : <StrategyResearchBeta capabilities={researchCapabilitiesQuery.data} prefill={handoffPrefill} />
            ) : (
              <div className="grid gap-6 lg:grid-cols-[1.25fr_0.75fr]">
                <BacktestForm options={optionsQuery.data} onCreate={submitCreate} creating={createMutation.isPending} createFeedback={createFeedback} requestedBotId={requestedBotId} />
                <JobHistory jobs={jobsQuery.data || []} loading={jobsQuery.isLoading} />
              </div>
            )}
            <section className="mt-8 rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
              <strong>Important:</strong> Historical performance does not guarantee future results. Results depend on the selected recorded dataset, feed, strategy version, slippage, and commission assumptions. A Backtest cannot place Alpaca Paper or live orders.
            </section>
          </>
        )}
      </main>
    </div>
  );
};

export default BacktestsPage;
